import { ConflictedAuthenticationResult, PostReviewRequest } from '../../generated/api/models.js';
import { DialogType } from '../../common/enums.js';
import {
  TypeKeys,
  checkValidEmployeeSubscription,
  dispatchAuthAction,
  getEmployeeSubscriptionsFailed,
  getEmployeeSubscriptionsFulfilled,
  resendEmailVerificationLinkFailed,
  resendEmailVerificationLinkFulfilled,
  reviewFailed,
  reviewFulfilled,
} from '../actions/index.js';
import { actionToActionState, getMdmIdHeaderFromUrlParams } from './epicUtils.js';
import { callUiApi, prepareUiApiRequest } from '../common/uiApiUtils.js';
import { combineEpics, ofType } from 'redux-observable';
import { concatMap, filter, ignoreElements, map, mergeMap, tap } from 'rxjs/operators';
import { empty, of } from 'rxjs';
import { getEmployeeSubscriptionType } from '../../public/common/util/category';
import {
  getSubscriptionsPrivateMethod,
  resendVerifyEmailLinkPrivateMethod,
  reviewAsEmployerPrivateMethod,
} from '../../generated/api/uiApiMethods.js';
import { isEmployeePortal } from '../../common/utils/browserUtils.js';
import { paths } from '../../common/constants/pathVariables.js';
import { push } from 'redux-first-history';
import { t } from '../../common/i18n/index.js';
import type { Action } from 'redux';
import type { ActionAndState, EpicDependencies } from './epicUtils.js';
import type { ActionsObservable, Epic, StateObservable } from 'redux-observable';
import type { CommonError } from '../../common/types/errors.js';
import type { DialogParams } from '../../common/types/dialog.js';
import type {
  GetEmployeeSubscriptionsAction,
  GetEmployeeSubscriptionsFulfilledAction,
  ResendEmailVerificationLinkAction,
  ReviewAction,
  ReviewFulfilledAction,
  SelfServiceActionTypes,
} from '../actions/index.js';
import type { State } from '../common/store.js';
import type { SubscriptionCategory } from '../../common/enums.js';
import type { SubscriptionRedeemReview, SubscriptionsResponse } from '../../generated/api/models.js';

// state key
export const AUTHENTICATED = 'authenticated';

const getEmployeeSubscriptionsQueryParams = (subscriptionType: SubscriptionCategory) => {
  return {
    details: true,
    subscriptionType: getEmployeeSubscriptionType(subscriptionType),
  };
};

export const getConflictingErrors = (
  errors?: CommonError[]
): { type: ConflictedAuthenticationResult.TypeEnum; conflictingAccountName?: string } | undefined => {
  const { conflictingAccountName, type } = (errors?.[0].source?.error || {}) as ConflictedAuthenticationResult;
  switch (type) {
    case ConflictedAuthenticationResult.TypeEnum.CONFLICTING_ACCOUNT:
      return { type, conflictingAccountName };
    case ConflictedAuthenticationResult.TypeEnum.DUPLICATE_CONTACT:
    case ConflictedAuthenticationResult.TypeEnum.POSSIBLE_CONTACT_DUPLICATE:
    case ConflictedAuthenticationResult.TypeEnum.CONTACT_SUPPORT:
    case ConflictedAuthenticationResult.TypeEnum.USER_NOT_FOUND:
    case ConflictedAuthenticationResult.TypeEnum.CONFLICTING_CONTACT:
      return { type };
    default:
      return undefined;
  }
};

export const getDuplicateContactDialogForCheckoutParams = (): DialogParams => ({
  type: DialogType.GENERIC_INFO_DIALOG,
  header: t.CVJF('We found a user with similar contact details'),
  body: t.RY1J(`We recommend to use unique email and phone number or select user from your company's user list.`),
});

// get user subscriptions
const getEmployeeSubscriptionsEpic: Epic<SelfServiceActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<SelfServiceActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(
    action$.pipe(ofType(TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS)),
    (action: GetEmployeeSubscriptionsAction) => actionToActionState(action, state$, AUTHENTICATED)
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const action = actionAndState.action as GetEmployeeSubscriptionsAction;
      return callUiApi({
        epicDependencies,
        state$,
        method: getSubscriptionsPrivateMethod(getEmployeeSubscriptionsQueryParams(action.subscriptionType)),
        successAction: (response: SubscriptionsResponse) =>
          getEmployeeSubscriptionsFulfilled(response, action.subscriptionType, action.subscriptionItemId),
        failureAction: getEmployeeSubscriptionsFailed,
      });
    })
  );

const completeGetEmployeeSubscriptionsEpic: Epic<SelfServiceActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<SelfServiceActionTypes>
) =>
  action$.ofType(TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS_FULLFILLED).pipe(
    mergeMap((action: GetEmployeeSubscriptionsFulfilledAction) => {
      if (action.subscriptionItemId !== undefined) {
        return of(checkValidEmployeeSubscription(action.subscriptionType, action.subscriptionItemId));
      }
      return empty();
    })
  );

const reviewEpic: Epic<SelfServiceActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<SelfServiceActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.REVIEW)), (action: ReviewAction) =>
    actionToActionState(action, state$, AUTHENTICATED)
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const reviewAction = actionAndState.action as ReviewAction;
      if (!reviewAction.request) {
        throw new Error('invalid action state for review, payload is missing');
      }
      const subscriptionId =
        reviewAction.reviewType === PostReviewRequest.ReviewTypeEnum.SUBSCRIPTION_REDEEM
          ? (reviewAction.request as SubscriptionRedeemReview).subscriptionId
          : undefined;
      return callUiApi({
        epicDependencies,
        state$,
        method: reviewAsEmployerPrivateMethod(),
        headers: getMdmIdHeaderFromUrlParams(),
        payload:
          reviewAction.reviewType === PostReviewRequest.ReviewTypeEnum.ONLINE_ORDER
            ? {
                onlineOrder: reviewAction.request,
                reviewType: reviewAction.reviewType,
                approve: reviewAction.approve,
                rejectReason: reviewAction.rejectReason,
              }
            : {
                subscriptionRedeem: reviewAction.request,
                reviewType: reviewAction.reviewType,
                approve: reviewAction.approve,
                rejectReason: reviewAction.rejectReason,
              },
        successAction: () =>
          reviewFulfilled(
            reviewAction.reviewType,
            reviewAction.approve,
            subscriptionId,
            reviewAction.customerOrderDisplayId,
            reviewAction.pendingSubscriptionActions
          ),
        failureAction: reviewFailed,
      });
    })
  );

export const completeReviewFulfilledEpic: Epic<SelfServiceActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<SelfServiceActionTypes>,
  state$: StateObservable<State>
) =>
  action$.pipe(
    ofType(TypeKeys.REVIEW_FULFILLED),
    filter(() =>
      state$.value.user && state$.value.user.authenticated
        ? state$.value.user.authenticated.errors === undefined
        : false
    ),
    map((action: ReviewFulfilledAction) => {
      if (isEmployeePortal(window.location.pathname)) {
        if (action.reviewType === PostReviewRequest.ReviewTypeEnum.SUBSCRIPTION_REDEEM) {
          return push(paths.EMPLOYEE_CHANGE_REQUEST_ORDERS);
        } else {
          return push(paths.EMPLOYEE_ORDERS);
        }
      }
      if (action.reviewType === PostReviewRequest.ReviewTypeEnum.ONLINE_ORDER) {
        return push(paths.CUSTOMER_ORDER_HOME);
      } else if (action.reviewType === PostReviewRequest.ReviewTypeEnum.SUBSCRIPTION_REDEEM) {
        return push(paths.CUSTOMER_ORDER_REDEEM_REQUESTS);
      } else {
        return push(paths.SELF_SERVICE_HOME);
      }
    })
  );

// Order a new link to verify email
export const resendEmailVerificationLinkEpic: Epic<SelfServiceActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<SelfServiceActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(
    action$.pipe(ofType(TypeKeys.RESEND_EMAIL_VERIFICATION_LINK)),
    (action: ResendEmailVerificationLinkAction) => actionToActionState(action, state$, AUTHENTICATED)
  ).pipe(
    concatMap(() => {
      return callUiApi({
        epicDependencies,
        state$,
        method: resendVerifyEmailLinkPrivateMethod(),
        successAction: resendEmailVerificationLinkFulfilled,
        failureAction: resendEmailVerificationLinkFailed,
      });
    })
  );

export const resetErrorsEpic: Epic<SelfServiceActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<SelfServiceActionTypes>
) =>
  action$.pipe(
    ofType(TypeKeys.RESET_ERRORS),
    tap(() => dispatchAuthAction('RESET_ERRORS')),
    ignoreElements()
  );

export const authenticatedUserEpic: Epic<SelfServiceActionTypes, Action, State, EpicDependencies> = combineEpics(
  getEmployeeSubscriptionsEpic,
  completeGetEmployeeSubscriptionsEpic,
  reviewEpic,
  completeReviewFulfilledEpic,
  resendEmailVerificationLinkEpic,
  resetErrorsEpic
);
